import { Component, signal, computed, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, Router, NavigationEnd, RouterLink } from '@angular/router';

import { filter } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { ChatBubble } from './shared/chat-bubble/chat-bubble';
import { ChatService } from './core/chat/chat.service';
import { SiteStatusService } from './core/site-status.service';
import { CookieConsentService } from './core/cookie-consent/cookie-consent.service';
import { CookieConsentBanner } from './shared/cookie-consent-banner/cookie-consent-banner';
import { Footer } from './shared/footer/footer';
import { Auth } from './core/auth/auth';
import { BillingService, BillingStatus } from './core/billing.service';
import { CardRequiredModal } from './shared/card-required-modal/card-required-modal';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, RouterLink, ChatBubble, CookieConsentBanner, Footer, MatIconModule, CardRequiredModal],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit, OnDestroy {
  protected readonly title = signal('Rebel Paint');
  private chatService = inject(ChatService);
  private router = inject(Router);
  protected siteStatus = inject(SiteStatusService);
  protected cookieConsent = inject(CookieConsentService);
  private auth = inject(Auth);
  private billingService = inject(BillingService);
  protected billingStatus = signal<BillingStatus | null>(null);

  /** Current path - updated on navigation so computed stays reactive */
  private currentPath = signal('');

  private sub: any;

  ngOnInit() {
    const initialPath = this.router.url || (typeof window !== 'undefined' ? window.location.pathname : '');
    this.currentPath.set(initialPath);
    this.chatService.setMessagesAppActive(initialPath.includes('/dashboard/messages'));
    this.sub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        const path = this.router.url;
        this.currentPath.set(path);
        this.chatService.setMessagesAppActive(path.includes('/dashboard/messages'));
        this.refreshBillingStatus();
      });
    this.refreshBillingStatus();
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  // Hide chat bubble on auth pages (login, signup, forgot, reset), share pages,
  // and the billing suspension page (chat APIs are hard-locked anyway).
  // Also fully gated behind the Messages initial-release feature flag (default
  // off) — mirrors the sidebar's messagesEnabled() gating from !110.
  showChatBubble = computed(() => {
    if (!this.siteStatus.messagesEnabled()) return false;
    const path = this.currentPath() || (typeof window !== 'undefined' ? window.location.pathname : this.router.url);
    if (path.startsWith('/share/')) return false;
    if (path.startsWith('/auth/')) return false;
    if (path.startsWith('/suspended')) return false;
    return this.chatService.shouldShowChatBubble();
  });

  /** True when on share page (vendor order view, etc.) - enables scrolling */
  isShareRoute = computed(() => {
    const path = this.currentPath() || (typeof window !== 'undefined' ? window.location.pathname : this.router.url);
    return path.startsWith('/share/');
  });

  /** True when on auth routes (login, signup, etc.) - never block with maintenance overlay so staff can sign in */
  isAuthRoute = computed(() => {
    const path = this.currentPath() || (typeof window !== 'undefined' ? window.location.pathname : this.router.url);
    return path.startsWith('/auth/');
  });

  /** True when on legal pages (privacy, terms, cookies) */
  isLegalRoute = computed(() => {
    const path = this.currentPath() || (typeof window !== 'undefined' ? window.location.pathname : this.router.url);
    return path.startsWith('/legal/');
  });

  /** True when footer should show (auth, legal, share - public pages) */
  showFooter = computed(() => this.isAuthRoute() || this.isLegalRoute() || this.isShareRoute());

  /** Billing admin page must stay usable so users can pay or add cards while locked. */
  isBillingAdminRoute = computed(() => {
    const path = this.currentPath() || (typeof window !== 'undefined' ? window.location.pathname : this.router.url) || '';
    return path.includes('/dashboard/admin/billing');
  });

  /**
   * Persistent early warning for billing admins while the org is past due but
   * not yet hard-locked, so suspension is never a surprise. (Hard lock itself
   * is handled by billingLockGuard / the 402 interceptor, which route to the
   * dedicated /suspended page.)
   */
  showBillingPastDueBanner = computed(() => {
    const path = this.currentPath() || (typeof window !== 'undefined' ? window.location.pathname : this.router.url) || '';
    if (!path.startsWith('/dashboard')) return false;
    const s = this.billingStatus();
    if (!s?.can_manage_billing) return false;
    return s.billing_lock_state === 'past_due_soft' || s.billing_lock_state === 'past_due_restricted';
  });

  billingDaysOverdue = computed(
    () => this.billingStatus()?.billing_days_overdue ?? this.billingService.hardLockDaysOverdue()
  );

  /** Total banner height in px for layout (regular banners + maintenance banner + past-due warning) */
  bannerHeight = computed(() => {
    let h = this.siteStatus.banners().length * 44;
    if (this.siteStatus.showMaintenanceBanner()) h += 44;
    if (this.showBillingPastDueBanner()) h += 44;
    return h;
  });

  private async refreshBillingStatus(): Promise<void> {
    if (!this.auth.isLoggedIn()) return;
    // GET /api/billing/status/ 404s when there's no X-Org header yet (the
    // backend can't resolve an org to check — see billing/views.py _get_org).
    // Profile selection can still be settling right after login/hydration,
    // so wait for it rather than firing a request that's guaranteed to 404
    // on every page load (quality-sweep item 11 background console noise).
    if (!this.auth.getSelectedProfile()) return;
    const path = this.currentPath() || this.router.url || '';
    if (!path.startsWith('/dashboard')) return;
    try {
      const status = await this.billingService.getBillingStatus();
      this.billingStatus.set(status);
      // Lock discovered mid-session (e.g. threshold crossed since login):
      // leave the workspace for the suspension page. The billing admin page
      // stays reachable so a locked admin can pay.
      if (status.billing_hard_locked && !this.isBillingAdminRoute()) {
        this.router.navigate(['/suspended']);
      }
    } catch {
      // Keep current state on transient errors.
    }
  }
}
